// @flow

import React, { useCallback, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType, VIDEO_PLAYER } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { Color, flex, Layout, maxWidthContainer, topBottomMargin, Width } from 'styles';
import { MarketsHero } from 'components/Content';
import { ImageLoader, Link } from 'components/Gui';
import { ModalContextActions, useModalDispatch } from 'contexts';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex('flex-start', 'flex-start')};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
  background: white;
`;

const Data = {
  fxFees: {
    type: 'fxFees',
    blurb: 'Commissions on single stocks cfds  - we charge $8 per standard lot per side ($16 R/T)  std lot = 100,000',
  },
  exchange: {
    type: 'exchange',
    label: 'Fees By Exchange',
    fees: [
      {
        exchange: 'CBOT',
        code: 'SR',
        description: 'US 10-YEAR INTEREST RATE SWAP',
        exchangeFee: '$0.80 ',
        marginRate: '$3,550 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'UB',
        description: 'ULTRA T-BOND',
        exchangeFee: '$0.85 ',
        marginRate: '$12,200 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'XC',
        description: 'MINI-SIZED CORN',
        exchangeFee: '$1.03 ',
        marginRate: '$250 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'XK',
        description: 'MINI-SIZED SOYBEANS',
        exchangeFee: '$1.03 ',
        marginRate: '$338 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'XW',
        description: 'MINI-SIZED WHEAT',
        exchangeFee: '$1.03 ',
        marginRate: '$285 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'YM',
        description: 'DOW JONES $5 MINI',
        exchangeFee: '$1.23 ',
        marginRate: '$12,000 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'Z3N',
        description: 'US 3-YEAR US TREASURY NOTES',
        exchangeFee: '$0.80 ',
        marginRate: '$700 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'ZB',
        description: 'US 30-YEAR TREASURY BOND',
        exchangeFee: '$0.80 ',
        marginRate: '$6,500 ',
        executionFee: '1.99',
      },
      { exchange: 'CBOT', code: 'ZC', description: 'CORN', exchangeFee: '$1.95 ', marginRate: '$1,000 ', executionFee: '1.99' },
      { exchange: 'CBOT', code: 'ZE', description: 'ETHANOL', exchangeFee: '$1.95 ', marginRate: '$4,350 ', executionFee: '1.99' },
      {
        exchange: 'CBOT',
        code: 'ZF',
        description: 'US 5-YEAR TREASURY NOTE',
        exchangeFee: '$0.65 ',
        marginRate: '$925 ',
        executionFee: '1.99',
      },
      { exchange: 'CBOT', code: 'ZL', description: 'SOYBEAN OIL', exchangeFee: '$1.95 ', marginRate: '$700 ', executionFee: '1.99' },
      {
        exchange: 'CBOT',
        code: 'ZM',
        description: 'SOYBEAN MEAL',
        exchangeFee: '$1.95 ',
        marginRate: '$1,020 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CBOT',
        code: 'ZN',
        description: 'US 10-YEAR TREASURY NOTE',
        exchangeFee: '$0.75 ',
        marginRate: '$2,000 ',
        executionFee: '1.99',
      },
      { exchange: 'CBOT', code: 'ZO', description: 'OATS', exchangeFee: '$1.95 ', marginRate: '$650 ', executionFee: '1.99' },
      {
        exchange: 'CBOT',
        code: 'ZQ',
        description: 'FED FUNDS 30-DAY INTEREST RATE',
        exchangeFee: '$0.75 ',
        marginRate: '$195 ',
        executionFee: '1.99',
      },
      { exchange: 'CBOT', code: 'ZR', description: 'ROUGH RICE', exchangeFee: '$1.95 ', marginRate: '$900 ', executionFee: '1.99' },
      { exchange: 'CBOT', code: 'ZS', description: 'SOYBEANS', exchangeFee: '$1.95 ', marginRate: '$1,650 ', executionFee: '1.99' },
      {
        exchange: 'CBOT',
        code: 'ZT',
        description: 'US 2-YEAR TREASURY NOTE',
        exchangeFee: '$0.65 ',
        marginRate: '$610 ',
        executionFee: '1.99',
      },
      { exchange: 'CBOT', code: 'ZW', description: 'WHEAT', exchangeFee: '$1.95 ', marginRate: '$1,425 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: '6A',
        description: 'AUSTRALIAN DOLLAR',
        exchangeFee: '$1.60 ',
        marginRate: '$1,800 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: '6B',
        description: 'BRITISH POUND',
        exchangeFee: '$1.60 ',
        marginRate: '$2,700 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: '6C',
        description: 'CANADIAN DOLLAR',
        exchangeFee: '$1.60 ',
        marginRate: '$1,550 ',
        executionFee: '1.99',
      },
      { exchange: 'CME', code: '6E', description: 'EUROFX', exchangeFee: '$1.60 ', marginRate: '$2,275 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: '6J',
        description: 'JAPANESE YEN',
        exchangeFee: '$1.60 ',
        marginRate: '$4,000 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: '6M',
        description: 'MEXICAN PESO',
        exchangeFee: '$1.60 ',
        marginRate: '$1,990 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: '6N',
        description: 'NEW ZEALAND DOLLAR',
        exchangeFee: '$1.60 ',
        marginRate: '$1,500 ',
        executionFee: '1.99',
      },
      { exchange: 'CME', code: '6S', description: 'SWISS FRANC', exchangeFee: '$1.60 ', marginRate: '$4,500 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: 'DC',
        description: 'MILK CLASS III',
        exchangeFee: '$2.40 ',
        marginRate: '$720 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'E7',
        description: 'E-MINI EUROFX',
        exchangeFee: '$0.85 ',
        marginRate: '$1,138 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'EMD',
        description: 'E-MINI S&P MIDCAP 400',
        exchangeFee: '$1.18 ',
        marginRate: '$17,000 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'ES',
        description: 'E-MINI S&P 500 INDEX',
        exchangeFee: '$1.18 ',
        marginRate: '$12,000 ',
        executionFee: '1.99',
      },
      { exchange: 'CME', code: 'GE', description: 'EURODOLLAR', exchangeFee: '$2.40 ', marginRate: '$625 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: 'GF',
        description: 'FEEDER CATTLE',
        exchangeFee: '$2.03 ',
        marginRate: '$4,860 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'GLB',
        description: 'LIBOR ONE MONTH',
        exchangeFee: '$2.40 ',
        marginRate: '$2,200 ',
        executionFee: '1.99',
      },
      { exchange: 'CME', code: 'HE', description: 'LEAN HOGS', exchangeFee: '$2.03 ', marginRate: '$2,850 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: 'J7',
        description: 'E-MINI JAPANESE YEN',
        exchangeFee: '$0.85 ',
        marginRate: '$2,000 ',
        executionFee: '1.99',
      },
      { exchange: 'CME', code: 'LBS', description: 'LUMBER', exchangeFee: '$2.03 ', marginRate: '$3,500 ', executionFee: '1.99' },
      { exchange: 'CME', code: 'LE', description: 'LIVE CATTLE', exchangeFee: '$2.03 ', marginRate: '$2,150 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: 'M2K',
        description: 'MICRO E-MINI RUSSELL 2000',
        exchangeFee: '$0.20 ',
        marginRate: '$1,500 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'M6A',
        description: 'E-MICRO AUD/USD',
        exchangeFee: '$0.16 ',
        marginRate: '$180 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CME',
        code: 'M6B',
        description: 'E-MICRO GBP/USD',
        exchangeFee: '$0.16 ',
        marginRate: '$270 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CME',
        code: 'M6E',
        description: 'E-MICRO EUR/USD',
        exchangeFee: '$0.16 ',
        marginRate: '$228 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CME',
        code: 'MCD',
        description: 'E-MICRO CAD/USD',
        exchangeFee: '$0.16 ',
        marginRate: '$155 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CME',
        code: 'MES',
        description: 'MICRO E-MINI S&P 500',
        exchangeFee: '$0.20 ',
        marginRate: '$1,200 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'MJY',
        description: 'E-MICRO JPY/USD',
        exchangeFee: '$0.16 ',
        marginRate: '$335 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CME',
        code: 'MNF',
        description: 'E-MICRO S&P CNX NIFTY 50 INDEX',
        exchangeFee: '$0.16 ',
        marginRate: '$1,500 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CME',
        code: 'MNQ',
        description: 'MICRO E-MINI NASDAQ-100',
        exchangeFee: '$0.20 ',
        marginRate: '$1,500 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'MSF',
        description: 'E-MICRO CHF/USD',
        exchangeFee: '$0.16 ',
        marginRate: '$450 ',
        executionFee: '0.99',
      },
      {
        exchange: 'CBOT',
        code: 'MYM',
        description: 'MICRO E-MINI DOW',
        exchangeFee: '$0.20 ',
        marginRate: '$1,200 ',
        executionFee: '1.99',
      },
      { exchange: 'CME', code: 'NKD', description: 'NIKKEI 225', exchangeFee: '$1.00 ', marginRate: '$9,000 ', executionFee: '1.99' },
      {
        exchange: 'CME',
        code: 'NQ',
        description: 'E-MINI NASDAQ 100 INDEX',
        exchangeFee: '$1.18 ',
        marginRate: '$15,000 ',
        executionFee: '1.99',
      },
      {
        exchange: 'CME',
        code: 'RTY',
        description: 'RUSSELL 2000 INDEX MINI ($50)',
        exchangeFee: '$1.18 ',
        marginRate: '$7,100 ',
        executionFee: '0.99',
      },
      { exchange: 'COMEX', code: 'GC', description: 'GOLD', exchangeFee: '$1.45 ', marginRate: '$9,150 ', executionFee: '1.99' },
      { exchange: 'COMEX', code: 'HG', description: 'COPPER', exchangeFee: '$1.45 ', marginRate: '$3,750 ', executionFee: '1.99' },
      {
        exchange: 'COMEX',
        code: 'MGC',
        description: 'E-MICRO GOLD',
        exchangeFee: '$0.75 ',
        marginRate: '$915 ',
        executionFee: '0.99',
      },
      {
        exchange: 'COMEX',
        code: 'QC',
        description: 'MINY COPPER',
        exchangeFee: '$0.75 ',
        marginRate: '$1,350 ',
        executionFee: '1.99',
      },
      {
        exchange: 'COMEX',
        code: 'MQI',
        description: 'MINY SILVER',
        exchangeFee: '$0.75 ',
        marginRate: '$4,500 ',
        executionFee: '1.99',
      },
      {
        exchange: 'COMEX',
        code: 'MQO',
        description: 'MINY GOLD',
        exchangeFee: '$0.75 ',
        marginRate: '$4,575 ',
        executionFee: '1.99',
      },
      { exchange: 'COMEX', code: 'SI', description: 'SILVER', exchangeFee: '$1.45 ', marginRate: '$9,000 ', executionFee: '1.99' },
      {
        exchange: 'COMEX',
        code: 'SIL',
        description: '1000 OZ SILVER',
        exchangeFee: '$0.75 ',
        marginRate: '$1,600 ',
        executionFee: '0.99',
      },
      { exchange: 'NYMEX', code: 'CL', description: 'CRUDE OIL', exchangeFee: '$1.50 ', marginRate: '$8,500 ', executionFee: '1.99' },
      {
        exchange: 'NYMEX',
        code: 'HO',
        description: 'HEATING OIL',
        exchangeFee: '$1.50 ',
        marginRate: '$4,600 ',
        executionFee: '1.99',
      },
      {
        exchange: 'NYMEX',
        code: 'NG',
        description: 'NATURAL GAS',
        exchangeFee: '$1.50 ',
        marginRate: '$1,650 ',
        executionFee: '1.99',
      },
      { exchange: 'NYMEX', code: 'PL', description: 'PLATINUM', exchangeFee: '$1.55 ', marginRate: '$4,500 ', executionFee: '1.99' },
      {
        exchange: 'NYMEX',
        code: 'QG',
        description: 'E-MINI NATURAL GAS',
        exchangeFee: '$0.50 ',
        marginRate: '$325 ',
        executionFee: '1.99',
      },
      {
        exchange: 'NYMEX',
        code: 'QH',
        description: 'E-MINI HEATING OIL',
        exchangeFee: '$1.20 ',
        marginRate: '$2,300 ',
        executionFee: '1.99',
      },
      {
        exchange: 'NYMEX',
        code: 'QM',
        description: 'E-MINI CRUDE LIGHT',
        exchangeFee: '$1.20 ',
        marginRate: '$4,250 ',
        executionFee: '1.99',
      },
      {
        exchange: 'NYMEX',
        code: 'QU',
        description: 'E-MINI UNLEADED GASOLINE',
        exchangeFee: '$1.20 ',
        marginRate: '$2,300 ',
        executionFee: '1.99',
      },
      {
        exchange: 'NYMEX',
        code: 'RT ',
        description: 'NY HARBOR RBOB GASOLINE',
        exchangeFee: '$1.50 ',
        marginRate: '$9,000 ',
        executionFee: '1.99',
      },
    ],
  },
};

const DataContainer = styled.div`
  padding: 48px 0;
  ${maxWidthContainer(800)};
  overflow-x: auto;

  > div {
    min-width: 500px;
    max-width: 800px;
    h2 {
      margin-bottom: 24px;
    }
    div.links {
      ${flex('flex-start', 'flex-start')};
    }
    div.row {
      ${flex('flex-start', 'flex-start')};

      &:nth-child(even) {
        background: ${Color.GREY_LIGHT};
      }

      > div {
        width: 180px;
        padding: 12px;
        box-sizing: border-box;
      }
    }
    div.header {
      margin-top: 24px;
      > div {
        font-weight: 700;
      }
    }
  }
`;

const LinkButton = styled(Link)`
  width: 180px;
  padding: 12px 0;
  border: 1px solid ${Color.MARKETS_RED};
  border-right: none;
  color: ${({ active }) => (active ? Color.WHITE : Color.BLACK_SOUL)};
  background: ${({ active }) => (active ? Color.MARKETS_RED : 'none')};

  &:last-of-type {
    border: 1px solid ${Color.MARKETS_RED};
  }
`;

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);
  const [sortedData, setSortedData] = useState({});
  const [dataKeys, setDataKeys] = useState(['all']);
  const [current, setCurrent] = useState('all');
  useEffect(() => {
    const parsedData = Data.exchange.fees.reduce(
      (acc, { exchange, ...rest }) => {
        acc.all.push(rest);
        if (!acc[exchange]) acc[exchange] = [];
        acc[exchange].push(rest);
        acc.all.sort(({ description: a }, { description: b }) => {
          const nameA = a.toUpperCase(); // ignore upper and lowercase
          const nameB = b.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        acc[exchange].sort(({ description: a }, { description: b }) => {
          const nameA = a.toUpperCase(); // ignore upper and lowercase
          const nameB = b.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        return acc;
      },
      {
        all: [],
      }
    );
    setSortedData(parsedData);
    setDataKeys(['all', ...Object.keys(parsedData).filter((name) => name !== 'all')]);
  }, []);
  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('pricing'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <MarketsHero
          title="PRICING"
          blurb="Fee Structures and Pricing"
          image={{
            name: 'pricing',
          }}
          images={data}
        />
        <DataContainer>
          <div>
            <h2>FUTURES</h2>
            <div className="links">
              {dataKeys.map((key) => (
                <LinkButton onClick={() => setCurrent(key)} key={`Group-${key}`} active={key === current}>
                  {key}
                </LinkButton>
              ))}
            </div>
            <div className="data">
              <div className="row header">
                <div>CODE</div>
                <div>DESCRIPTION</div>
                <div>EXCHANGE FEE</div>
                <div>MARGIN RATE</div>
                <div>
                  INTRADAY MARGIN RATE<sup>1</sup>
                </div>
                <div>
                  EXECUTION FEE<sup>2</sup>
                </div>
              </div>
              {sortedData[current] &&
                sortedData[current].map(({ code, description, exchangeFee, marginRate, executionFee }) => (
                  <div className="row">
                    <div>{code}</div>
                    <div>{description}</div>
                    <div>{exchangeFee}</div>
                    <div>{marginRate}</div>
                    <div>50%</div>
                    <div>{executionFee}</div>
                  </div>
                ))}
            </div>
            <p
              style={{
                marginTop: '20px',
              }}>
              <sup>1</sup>Customer is responsible for ensuring their trading account is fully margined by End of Day otherwise a
              liquidation <Link url={`/fees`}>fee</Link> of $25 per contract will be charged.
              <br />
              <sup>2</sup>10 commission free trades per account per month, capped at a 10 lot total. Customer can do (1) 10 lot or (10)
              1ot trades after reaching the cap the standard pricing applies. The Standard Pricing is $1.99 per contract per side for
              standard and mini lots and $0.99 per contract per side for micro lots.
            </p>
          </div>
        </DataContainer>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    imageToWebP: allFile(filter: { relativeDirectory: { in: ["pages/team", "pages/pricing"] }, extension: { nin: ["svg", "gif"] } }) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(filter: { relativeDirectory: { eq: "pages/team" }, extension: { in: ["svg", "gif"] } }) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
